<template>
  <div class="cashback-pagina">
    <div v-show="!carteira">
      <Loading :isActive="loading && !jaCarregou" />

      <div class="cashback-aviso">
        <fa-icon icon="info-circle" class="cashback-aviso__icone" />
        <span>Clientes com movimentação de cashback. Clique em um cliente para ver o extrato completo.</span>
      </div>

      <div class="cashback-filtros">
        <div class="cashback-filtros__linha">
          <div class="cashback-busca">
            <input
              class="form-control"
              v-model="filtros.q"
              placeholder="Buscar cliente por nome, código ou CNPJ..."
              aria-label="Buscar cliente por nome, código ou CNPJ"
              @input="loadComDebounce()"
            />
            <fa-icon v-if="atualizando" icon="spinner" spin class="cashback-busca__indicador" />
          </div>
          <div class="cashback-filtros__acoes">
            <CButton
              color="primary"
              :variant="filtrosAbertos ? null : 'outline'"
              class="cashback-botao-texto"
              @click="filtrosAbertos = !filtrosAbertos"
            >
              <fa-icon icon="filter" /> Filtros
              <span
                v-if="totalFiltrosAtivos"
                :class="['cashback-filtros__contador', { 'cashback-filtros__contador--fechado': !filtrosAbertos }]"
              >
                {{ totalFiltrosAtivos }}
              </span>
            </CButton>
          </div>
        </div>

        <div class="cashback-filtros__painel" v-if="filtrosAbertos">
          <div v-if="erroHierarquia" class="cashback-filtros__aviso">
            <fa-icon icon="exclamation-triangle" class="mr-1" />
            Não foi possível carregar gerentes, supervisores, operadores e representantes: {{ erroHierarquia }}
            <button type="button" class="cashback-chips__limpar" @click="carregarHierarquia">Tentar novamente</button>
          </div>
          <CRow>
            <CCol md="4">
              <div class="cashback-campo">
                <label class="cashback-campo__rotulo">Saldo</label>
                <select class="form-control" v-model="filtros.saldo" @change="load()">
                  <option value="">Todos</option>
                  <option v-for="(rotulo, chave) in situacoesSaldo" :key="chave" :value="chave">{{ rotulo }}</option>
                </select>
              </div>
            </CCol>
            <CCol md="4">
              <div class="cashback-campo">
                <label class="cashback-campo__rotulo">Última movimentação de</label>
                <input class="form-control" type="date" v-model="filtros.dtIni" @change="load()" />
              </div>
            </CCol>
            <CCol md="4">
              <div class="cashback-campo">
                <label class="cashback-campo__rotulo">Até</label>
                <input class="form-control" type="date" v-model="filtros.dtFim" @change="load()" />
              </div>
            </CCol>
          </CRow>
          <CRow>
            <CCol md="3">
              <div class="cashback-campo">
                <label class="cashback-campo__rotulo">Gerente</label>
                <select class="form-control" v-model="filtros.gerente_id" @change="aoMudarHierarquia('gerente_id')">
                  <option value="">Todos</option>
                  <option v-for="gerente in hierarquia.gerentes" :key="gerente.id" :value="gerente.id">{{ gerente.nome }}</option>
                </select>
              </div>
            </CCol>
            <CCol md="3">
              <div class="cashback-campo">
                <label class="cashback-campo__rotulo">Supervisor</label>
                <select class="form-control" v-model="filtros.supervisor_id" @change="aoMudarHierarquia('supervisor_id')">
                  <option value="">Todos</option>
                  <option v-for="supervisor in supervisoresDisponiveis" :key="supervisor.id" :value="supervisor.id">{{ supervisor.nome }}</option>
                </select>
              </div>
            </CCol>
            <CCol md="3">
              <div class="cashback-campo">
                <label class="cashback-campo__rotulo">Operador</label>
                <select class="form-control" v-model="filtros.operador_id" @change="load()">
                  <option value="">Todos</option>
                  <option v-for="operador in operadoresDisponiveis" :key="operador.id" :value="operador.id">{{ operador.nome }}</option>
                </select>
              </div>
            </CCol>
            <CCol md="3">
              <div class="cashback-campo">
                <label class="cashback-campo__rotulo">Representante</label>
                <select class="form-control" v-model="filtros.representante_id" @change="load()">
                  <option value="">Todos</option>
                  <option v-for="representante in representantesDisponiveis" :key="representante.id" :value="representante.id">{{ representante.nome }}</option>
                </select>
              </div>
            </CCol>
          </CRow>
        </div>

        <div class="cashback-chips" v-if="chipsFiltros.length">
          <span class="cashback-chips__titulo">Filtrando por</span>
          <span class="cashback-chip" v-for="chip in chipsFiltros" :key="chip.chave">
            <strong>{{ chip.rotulo }}:</strong> {{ chip.valor }}
            <button
              type="button"
              class="cashback-chip__remover"
              title="Remover filtro"
              :aria-label="`Remover filtro ${chip.rotulo}`"
              @click="removerFiltro(chip.chave)"
            >
              &times;
            </button>
          </span>
          <button type="button" class="cashback-chips__limpar" @click="limparFiltros()">limpar tudo</button>
        </div>
      </div>

      <div :class="['cashback-tabela-caixa', { 'cashback-tabela-caixa--atualizando': atualizando }]">
        <table class="cashback-tabela">
          <thead>
            <tr>
              <th>Cliente</th>
              <th style="width: 140px">Saldo</th>
              <th style="width: 190px">Próximo vencimento</th>
              <th style="width: 170px">Última movimentação</th>
              <th style="width: 110px">Movimentos</th>
              <th style="width: 40px"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="erroCarga">
              <td colspan="6" class="cashback-tabela__vazio">
                <div class="cashback-tabela__erro">Não foi possível carregar as carteiras: {{ erroCarga }}</div>
                <CButton color="primary" variant="outline" size="sm" @click="load(paginaAtual)">Tentar novamente</CButton>
              </td>
            </tr>
            <tr v-else-if="items.length === 0">
              <td colspan="6" class="cashback-tabela__vazio">Nenhum cliente com movimentação de cashback para os filtros</td>
            </tr>
            <tr
              v-for="item in items"
              :key="item.clienteId"
              class="cashback-carteiras__linha"
              tabindex="0"
              @click="abrir(item)"
              @keydown.enter="abrir(item)"
            >
              <td>
                <div class="cashback-carteiras__nome">
                  {{ item.nome || `Cliente ${item.clienteId}` }}
                  <span v-if="item.bloqueado" class="cashback-etiqueta cashback-etiqueta--bloqueado"><fa-icon icon="ban" class="mr-1" />Bloqueado</span>
                </div>
                <div class="cashback-tabela__secundario">
                  Código {{ item.clienteId }}
                  <template v-if="item.cnpj"> · {{ formatDocumento(item.cnpj) }}</template>
                  <template v-if="item.cidade"> · {{ item.cidade }}<template v-if="item.uf">/{{ item.uf }}</template></template>
                </div>
              </td>
              <td :class="classeSaldo(item.saldo)">{{ formatMoney(item.saldo) }}</td>
              <td>
                <template v-if="item.proximoVencimento">
                  {{ formatMoney(item.proximoVencimento.valor) }}
                  <div class="cashback-tabela__secundario">em {{ formatData(item.proximoVencimento.data) }}</div>
                </template>
                <span v-else class="cashback-tabela__secundario">—</span>
              </td>
              <td>{{ item.ultimoMovimento | datetime }}</td>
              <td>{{ item.quantidadeMovimentos }}</td>
              <td class="cashback-carteiras__seta"><fa-icon icon="chevron-right" /></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="cashback-paginacao" v-if="paginator.last_page > 1">
        <pagination :data="paginator" :limit="5" @pagination-change-page="load" />
      </div>
    </div>

    <ExtratoCarteira v-if="carteira" :carteira="carteira" @voltar="carteira = null" @alterado="load(paginaAtual)" />
  </div>
</template>

<script>
import _debounce from "lodash/debounce";
import { get } from "@/helpers/apiRequest";
import Loading from "../../components/Loading";
import listagemMixin from "@/mixins/listagemMixin";
import ExtratoCarteira from "./components/ExtratoCarteira.vue";
import { DEBOUNCE_BUSCA_MS, SITUACOES_SALDO, classeSaldo, formatData, formatDocumento, formatMoney, mensagemErroApi } from "./helpers";

export default {
  name: "cashback-carteiras",
  components: { Loading, ExtratoCarteira },
  mixins: [listagemMixin],

  data() {
    return {
      listagemEndpoint: "/admin/cashback/carteiras",
      carteira: null,
      jaCarregou: false,
      erroCargaNaTela: true,
      erroHierarquia: null,
      filtros: { q: "", saldo: "", dtIni: "", dtFim: "", gerente_id: "", supervisor_id: "", operador_id: "", representante_id: "" },
      rotulosFiltros: {
        q: "Busca",
        saldo: "Saldo",
        dtIni: "Movimentação de",
        dtFim: "Até",
        gerente_id: "Gerente",
        supervisor_id: "Supervisor",
        operador_id: "Operador",
        representante_id: "Representante",
      },
      hierarquia: { gerentes: [], supervisores: [], operadores: [], representantes: [] },
      situacoesSaldo: SITUACOES_SALDO,
    };
  },

  computed: {
    supervisoresDisponiveis() {
      const { gerente_id: gerente } = this.filtros;

      return gerente ? this.hierarquia.supervisores.filter((item) => item.superior === gerente) : this.hierarquia.supervisores;
    },

    supervisoresDoFiltro() {
      if (this.filtros.supervisor_id) return [this.filtros.supervisor_id];
      if (this.filtros.gerente_id) return this.supervisoresDisponiveis.map((item) => item.id);

      return null;
    },

    operadoresDisponiveis() {
      return this.daEquipe(this.hierarquia.operadores);
    },

    representantesDisponiveis() {
      return this.daEquipe(this.hierarquia.representantes);
    },

    atualizando() {
      return this.loading && this.jaCarregou;
    },

    paginaAtual() {
      return this.paginator.current_page || 1;
    },
  },

  watch: {
    loading(carregando) {
      if (!carregando) this.jaCarregou = true;
    },
  },

  mounted() {
    this.carregarHierarquia();
  },

  methods: {
    carregarHierarquia() {
      this.erroHierarquia = null;

      return get("/admin/cashback/carteiras/opcoes")
        .then((json) => {
          this.hierarquia = { ...this.hierarquia, ...json };
        })
        .catch((err) => {
          this.erroHierarquia = mensagemErroApi(err);
        });
    },

    daEquipe(lista) {
      const supervisores = this.supervisoresDoFiltro;

      return supervisores ? lista.filter((item) => supervisores.includes(item.superior)) : lista;
    },

    aoMudarHierarquia(campo) {
      if (campo === "gerente_id" && !this.supervisoresDisponiveis.some((item) => item.id === this.filtros.supervisor_id)) {
        this.filtros.supervisor_id = "";
      }

      if (!this.operadoresDisponiveis.some((item) => item.id === this.filtros.operador_id)) this.filtros.operador_id = "";
      if (!this.representantesDisponiveis.some((item) => item.id === this.filtros.representante_id)) {
        this.filtros.representante_id = "";
      }

      this.load();
    },

    nomeNaHierarquia(lista, id) {
      const item = lista.find((opcao) => opcao.id === String(id));

      return item ? item.nome : id;
    },

    appendFiltros(params) {
      Object.entries(this.filtros).forEach(([chave, valor]) => {
        if (valor !== "" && valor !== null) params.append(chave, valor);
      });
    },

    descreverFiltro(chave, valor) {
      if (chave === "saldo") return SITUACOES_SALDO[valor] || valor;
      if (chave === "dtIni" || chave === "dtFim") return formatData(valor);
      if (chave === "gerente_id") return this.nomeNaHierarquia(this.hierarquia.gerentes, valor);
      if (chave === "supervisor_id") return this.nomeNaHierarquia(this.hierarquia.supervisores, valor);
      if (chave === "operador_id") return this.nomeNaHierarquia(this.hierarquia.operadores, valor);
      if (chave === "representante_id") return this.nomeNaHierarquia(this.hierarquia.representantes, valor);

      return valor;
    },

    loadComDebounce: _debounce(function () {
      this.load();
    }, DEBOUNCE_BUSCA_MS),

    abrir(item) {
      this.carteira = item;
    },

    classeSaldo,

    formatData,
    formatDocumento,
    formatMoney,
  },
};
</script>

<style scoped src="./cashback.css"></style>

<style scoped>
.cashback-carteiras__linha {
  cursor: pointer;
}

.cashback-carteiras__linha:focus-visible {
  outline: 2px solid rgba(var(--cashback-cor-primaria-rgb), 0.4);
  outline-offset: -2px;
}

.cashback-carteiras__linha:hover {
  background-color: #f9fafb;
}

.cashback-carteiras__nome {
  font-weight: 600;
  color: #101828;
}

.cashback-carteiras__seta {
  color: #98a2b3;
  text-align: center;
}
</style>
